<template>
  <HrPageLayout
    title="My Payroll History"
    subtitle="View payment history and export records"
    icon="📜"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Payroll History' }
    ]"
  >
    <!-- Loading State -->
    <div v-if="loading" class="hr-loading-wrap">
      <div class="hr-spinner"></div>
      <p>Loading payroll history...</p>
    </div>

    <div v-else-if="historyData" class="history-content">
      <!-- Statistics Cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <p class="hr-metric-label">Total Payments</p>
          <h2 class="hr-metric-value">{{ historyData.statistics.totalPayments }}</h2>
          <small class="hr-text-muted">All time</small>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-wallet"></i></div>
          <p class="hr-metric-label">Total Earned</p>
          <h2 class="hr-metric-value">{{ formatCurrency(historyData.statistics.totalNet) }}</h2>
          <small class="hr-text-muted">Net pay</small>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-line-chart"></i></div>
          <p class="hr-metric-label">Average Net Pay</p>
          <h2 class="hr-metric-value">{{ formatCurrency(historyData.statistics.avgNet) }}</h2>
          <small class="hr-text-muted">Per month</small>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
          <p class="hr-metric-label">Latest Payment</p>
          <h6 class="hr-metric-value hr-fs-sm">{{ formatDate(historyData.statistics.latestPayment) }}</h6>
          <small class="hr-text-muted">Most recent</small>
        </div>
      </div>

      <!-- Filters & Actions -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid hr-gap-3">
            <div class="hr-filter-group">
              <label class="hr-form-label">Year</label>
              <select v-model="filters.year" @change="loadHistory" class="hr-select">
                <option value="">All Years</option>
                <option v-for="year in historyData.availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Month</label>
              <select v-model="filters.month" @change="loadHistory" class="hr-select">
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" @change="loadHistory" class="hr-select">
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Paid">Paid</option>
                <option value="Failed">Failed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div class="hr-filter-group hr-flex hr-gap-2">
              <button @click="resetFilters" class="hr-btn hr-btn-secondary">
                <i class="bx bx-refresh hr-me-1"></i>Reset
              </button>
              <button @click="exportHistory" class="hr-btn hr-btn-success">
                <i class="bx bx-export hr-me-1"></i>Export
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Years Summary -->
      <div v-if="historyData.yearsSummary && historyData.yearsSummary.length > 0" class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-bar-chart hr-me-2"></i>Yearly Summary</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
              <tr>
                <th>Year</th>
                <th class="hr-text-center">Payments</th>
                <th class="hr-text-end">Total Gross</th>
                <th class="hr-text-end">Total Deductions</th>
                <th class="hr-text-end">Total Net</th>
                <th class="hr-text-end">Avg. Monthly</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="year in historyData.yearsSummary" :key="year.year">
                <td><strong>{{ year.year }}</strong></td>
                <td class="hr-text-center">
                  <span class="hr-badge hr-badge-primary">{{ year.payment_count }}</span>
                </td>
                <td class="hr-text-end">{{ formatCurrency(year.total_gross) }}</td>
                <td class="hr-text-end hr-text-danger">{{ formatCurrency(year.total_deductions) }}</td>
                <td class="hr-text-end hr-text-success"><strong>{{ formatCurrency(year.total_net) }}</strong></td>
                <td class="hr-text-end hr-text-info">{{ formatCurrency(year.total_net / year.payment_count) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Payment History Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-history hr-me-2"></i>Payment History</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="historyData.history && historyData.history.length > 0" class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
              <tr>
                <th>Period</th>
                <th>Payment Date</th>
                <th class="hr-text-end">Gross Salary</th>
                <th class="hr-text-end">Deductions</th>
                <th class="hr-text-end">Net Salary</th>
                <th>Status</th>
                <th class="hr-text-center">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="payment in historyData.history" :key="payment.id">
                <td>
                  <div>
                    <h6 class="hr-mb-1">{{ payment.period_name }}</h6>
                    <p class="hr-text-muted hr-mb-0 hr-small">Ref: {{ payment.payment_reference || 'N/A' }}</p>
                  </div>
                </td>
                <td><span class="hr-text-muted">{{ payment.payment_date_formatted }}</span></td>
                <td class="hr-text-end"><strong>{{ formatCurrency(payment.gross_salary) }}</strong></td>
                <td class="hr-text-end hr-text-danger">{{ formatCurrency(payment.total_deductions) }}</td>
                <td class="hr-text-end hr-text-success"><strong>{{ formatCurrency(payment.net_salary) }}</strong></td>
                <td>
                  <span class="hr-badge" :class="getStatusBadgeClass(payment.payment_status)">{{ payment.payment_status }}</span>
                </td>
                <td class="hr-text-center">
                  <div class="hr-btn-group">
                    <button @click="viewDetails(payment)" class="hr-btn hr-btn-sm hr-btn-outline-primary" title="View Details">
                      <i class="bx bx-show"></i>
                    </button>
                    <button @click="viewPayslip(payment)" class="hr-btn hr-btn-sm hr-btn-outline-success" title="View Payslip">
                      <i class="bx bx-file"></i>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-wallet"></i></div>
            <h5>No payment history found</h5>
            <p class="hr-text-muted">Try adjusting your filters</p>
          </div>

          <!-- Pagination -->
          <div v-if="historyData.history && historyData.history.length > 0" class="hr-pagination-wrap hr-mt-4">
            <div class="hr-pagination-info">
              Showing {{ ((historyData.pagination.currentPage - 1) * historyData.pagination.limit) + 1 }}
              to {{ Math.min(historyData.pagination.currentPage * historyData.pagination.limit, historyData.pagination.totalRecords) }}
              of {{ historyData.pagination.totalRecords }} entries
            </div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="historyData.pagination.currentPage <= 1"
                      @click="changePage(historyData.pagination.currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <template v-for="page in getPageNumbers()" :key="page">
                <button v-if="page !== '...'" type="button" class="hr-page-btn"
                        :class="{ 'hr-page-active': page === historyData.pagination.currentPage }"
                        @click="changePage(page)">{{ page }}</button>
                <span v-else class="hr-page-ellipsis">...</span>
              </template>
              <button type="button" class="hr-page-btn"
                      :disabled="historyData.pagination.currentPage >= historyData.pagination.totalPages"
                      @click="changePage(historyData.pagination.currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <TransactionDetails ref="transactionDetailsModal" :transaction-id="selectedTransactionId" />
    <Payslip ref="payslipModal" :transaction-id="selectedPayslipId" />
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import TransactionDetails from "@/components/payroll/TransactionDetails.vue";
import Payslip from "@/components/payroll/Payslip.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeePayrollHistory",
  components: { HrPageLayout, TransactionDetails, Payslip },

  data() {
    return {
      loading: false,
      historyData: null,
      filters: { year: '', month: '', status: '', page: 1 },
      selectedTransactionId: null,
      selectedPayslipId: null
    };
  },

  mounted() {
    this.loadHistory();
  },

  methods: {
    async loadHistory() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getEmployeePayrollHistory");
        data.append("page", this.filters.page);
        data.append("limit", 20);
        if (this.filters.year) data.append("year", this.filters.year);
        if (this.filters.month) data.append("month", this.filters.month);
        if (this.filters.status) data.append("status", this.filters.status);

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.historyData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load payroll history');
        }
      } catch (error) {
        this.$toast?.error("Failed to load payroll history: " + error.message) || console.error("Failed to load payroll history:", error);
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.filters = { year: '', month: '', status: '', page: 1 };
      this.loadHistory();
    },

    changePage(page) {
      if (page < 1 || page > this.historyData.pagination.totalPages || page === '...') return;
      this.filters.page = page;
      this.loadHistory();
    },

    getPageNumbers() {
      const totalPages = this.historyData.pagination.totalPages;
      const currentPage = this.historyData.pagination.currentPage;
      if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
      if (currentPage <= 3) return [1, 2, 3, 4, '...', totalPages - 1, totalPages];
      if (currentPage >= totalPages - 2) return [1, 2, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    },

    viewDetails(payment) {
      this.selectedTransactionId = payment.id;
      this.$nextTick(() => {
        if (this.$refs.transactionDetailsModal) this.$refs.transactionDetailsModal.showModal(payment.id);
      });
    },

    viewPayslip(payment) {
      this.selectedPayslipId = payment.id;
      this.$nextTick(() => {
        if (this.$refs.payslipModal) this.$refs.payslipModal.showModal(payment.id);
      });
    },

    async exportHistory() {
      try {
        this.$toast?.info("Generating export...") || console.log("Generating export...");
        const exportData = this.historyData.history.map(payment => ({
          'Period': payment.period_name,
          'Payment Date': payment.payment_date_formatted,
          'Gross Salary': payment.gross_salary,
          'Total Deductions': payment.total_deductions,
          'Net Salary': payment.net_salary,
          'Status': payment.payment_status,
          'Payment Reference': payment.payment_reference || 'N/A'
        }));
        const csv = this.convertToCSV(exportData);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Payroll_History_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$toast?.success("Export downloaded successfully") || alert("Export downloaded successfully");
      } catch (error) {
        this.$toast?.error("Export failed: " + error.message) || alert("Export failed: " + error.message);
      }
    },

    convertToCSV(data) {
      if (!data || data.length === 0) return '';
      const headers = Object.keys(data[0]);
      const csvRows = data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','));
      return [headers.join(','), ...csvRows].join('\n');
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getStatusBadgeClass(status) {
      const map = { 'Pending': 'hr-badge-warning', 'Processing': 'hr-badge-info', 'Paid': 'hr-badge-success', 'Failed': 'hr-badge-danger', 'Cancelled': 'hr-badge-secondary' };
      return 'hr-badge ' + (map[status] || 'hr-badge-secondary');
    },
    goTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-fs-sm { font-size: 1rem; }
.hr-btn-group { display: flex; gap: 8px; justify-content: center; flex-wrap: wrap; }
.hr-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
.hr-filter-group { min-width: 0; }
.hr-gap-2 { gap: 8px; }

@media (max-width: 768px) {
  .hr-btn-group { flex-direction: column; }
}
</style>
